import { useEffect, useState, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Play, X, Layers } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { getApiUrl } from '../lib/api'
import { useAuthStore } from '../lib/auth'
import type { GroupedWatchHistory, WatchHistory } from '../../../shared/types/watch-history.types'
import SourceSelectionDialog from './SourceSelectionDialog'
import { useIsTVDevice, useTVRow } from '../hooks/useTVNavigation'

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w500'

export default function RecentlyWatchedRow() {
  const { getAuthHeaders } = useAuthStore()
  const [watchHistory, setWatchHistory] = useState<GroupedWatchHistory[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedMovie, setSelectedMovie] = useState<GroupedWatchHistory | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const navigate = useNavigate()
  const isTV = useIsTVDevice()
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const previousFocusedIndexRef = useRef<number>(-1)
  
  // Register row with navigation system (first row, rowIndex 0)
  const rowId = 'continue-watching'
  const { registerCard, unregisterCard } = useTVRow(
    rowId,
    0, // First row
    watchHistory.length,
    undefined, // No previous row
    'trending', // Next row is trending
    'hero-play-button' // Up target is hero buttons
  )
  
  // Register cards with navigation system
  useEffect(() => {
    if (!isTV || watchHistory.length === 0) return
    
    watchHistory.forEach((_, index) => {
      const ref = cardRefs.current[index]
      if (ref) {
        const cardRef = { current: ref } as React.RefObject<HTMLElement>
        registerCard(index, cardRef)
      }
    })
    
    return () => {
      watchHistory.forEach((_, index) => {
        unregisterCard(index)
      })
    }
  }, [isTV, watchHistory.length, registerCard, unregisterCard])
  
  // Clear focus ring when focusedIndex changes (ensures only one card has the ring)
  useEffect(() => {
    if (isTV && previousFocusedIndexRef.current !== -1 && previousFocusedIndexRef.current !== focusedIndex) {
      // Previous card lost focus - the className condition will handle hiding the ring
      // This effect ensures state is consistent
    }
    previousFocusedIndexRef.current = focusedIndex
  }, [isTV, focusedIndex])
  
  // Handle Enter/Space to select movie
  useEffect(() => {
    if (!isTV) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (focusedIndex === -1) return

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        if (focusedIndex >= 0 && focusedIndex < watchHistory.length) {
          handleCardClick(watchHistory[focusedIndex])
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isTV, focusedIndex, watchHistory])

  useEffect(() => {
    fetchWatchHistory()
  }, [])

  const fetchWatchHistory = async () => {
    try {
      setLoading(true)
      const headers = getAuthHeaders()
      const response = await fetch(getApiUrl('WATCH_HISTORY_RECENT_GROUPED'), {
        headers
      })
      const data = await response.json()

      if (data.success && data.data) {
        setWatchHistory(data.data)
      }
    } catch (error) {
      console.error('Failed to fetch watch history:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCardClick = (groupedMovie: GroupedWatchHistory) => {
    // Check both the flag and actual array length to ensure accuracy
    const hasMultiple = groupedMovie.has_multiple_sources && groupedMovie.all_sources.length > 1
    
    if (hasMultiple) {
      if (isTV) {
        // On TV, navigate to dedicated route instead of showing dialog
        navigate({
          to: '/watch-history-sources/$tmdbId',
          params: { tmdbId: groupedMovie.tmdb_id.toString() },
          search: {
            title: groupedMovie.title,
            sources: JSON.stringify(groupedMovie.all_sources),
            posterPath: groupedMovie.poster_path || '',
            backdropPath: groupedMovie.backdrop_path || '',
          }
        })
      } else {
        // On non-TV devices, show dialog
        setSelectedMovie(groupedMovie)
        setIsDialogOpen(true)
      }
    } else {
      // Resume directly with the only source (or primary source if multiple but should only use one)
      resumePlayback(groupedMovie.primary_source)
    }
  }

  const resumePlayback = (source: WatchHistory) => {
    // Close dialog if open
    setIsDialogOpen(false)
    
    // Resume playback based on source type
    switch (source.source_type) {
      case 'vidfast':
        // Navigate with all movie metadata for watch tracking
        navigate({
          to: '/watch/$tmdbId',
          params: { tmdbId: source.tmdb_id.toString() },
          search: {
            source: 'vidfast',
            title: source.title,
            posterPath: source.poster_path || '',
            backdropPath: source.backdrop_path || '',
            overview: source.overview || '',
            releaseDate: source.release_date || '',
            voteAverage: source.vote_average?.toString() || '',
            resumeFrom: source.playback_position_seconds
          }
        })
        break
      case 'vidlink':
        // Navigate with all movie metadata for watch tracking
        navigate({
          to: '/watch/$tmdbId',
          params: { tmdbId: source.tmdb_id.toString() },
          search: {
            source: 'vidlink',
            title: source.title,
            posterPath: source.poster_path || '',
            backdropPath: source.backdrop_path || '',
            overview: source.overview || '',
            releaseDate: source.release_date || '',
            voteAverage: source.vote_average?.toString() || '',
            resumeFrom: source.playback_position_seconds
          }
        })
        break
      
      case 'vidsrc':
        // Navigate with all movie metadata for watch tracking
        navigate({
          to: '/watch/$tmdbId',
          params: { tmdbId: source.tmdb_id.toString() },
          search: {
            source: 'vidsrc',
            title: source.title,
            posterPath: source.poster_path || '',
            backdropPath: source.backdrop_path || '',
            overview: source.overview || '',
            releaseDate: source.release_date || '',
            voteAverage: source.vote_average?.toString() || '',
            resumeFrom: source.playback_position_seconds
          }
        })
        break
      
      case 'filestream':
      case 'gdrive':
        // Navigate to video player (will auto-resume via watchTracker)
        navigate({
          to: '/',
          search: {
            playMovie: source.tmdb_id, // Pass as number, not string
            resumeFrom: source.playback_position_seconds
          } as any // Type assertion to avoid TS errors with search params
        })
        break
    }
  }

  const handleRemove = async (e: React.MouseEvent, tmdbId: number) => {
    e.stopPropagation()
    
    try {
      // Remove all sources for this movie
      const movieToRemove = watchHistory.find(m => m.tmdb_id === tmdbId)
      if (!movieToRemove) return

      // Delete all sources
      const headers = getAuthHeaders()
      await Promise.all(
        movieToRemove.all_sources.map(source =>
          fetch(getApiUrl('WATCH_HISTORY_REMOVE', source.id), {
            method: 'DELETE',
            headers
          })
        )
      )

      // Remove from UI
      setWatchHistory(prev => prev.filter(m => m.tmdb_id !== tmdbId))
    } catch (error) {
      console.error('Failed to remove from watch history:', error)
    }
  }

  // Don't render if no watch history
  if (!loading && watchHistory.length === 0) {
    return null
  }

  return (
    <>
      <div className="mb-8">
        <h2 className={`text-xl md:text-2xl font-semibold text-white mb-4 ${isTV ? '' : 'px-4 sm:px-6 lg:px-8'}`}>
          Continue Watching
        </h2>
        
        {loading ? (
          <div className="flex gap-2 md:gap-3 overflow-hidden px-4 sm:px-6 lg:px-8">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-[280px] sm:w-[320px] md:w-[360px] h-[158px] sm:h-[180px] md:h-[203px] bg-gray-800 rounded-md animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="relative">
            <div className={`flex overflow-x-auto pb-4 scrollbar-hide ${isTV ? 'movie-row-container scroll-container' : 'gap-2 md:gap-3 px-4 sm:px-6 lg:px-8'}`}>
              <AnimatePresence>
                {watchHistory.map((groupedMovie) => {
                  const primarySource = groupedMovie.primary_source
                  const progressPercentage = primarySource.total_duration_seconds > 0
                    ? Math.floor((primarySource.playback_position_seconds / primarySource.total_duration_seconds) * 100)
                    : 0

                  return (
                    <motion.div
                      key={groupedMovie.tmdb_id}
                      ref={(el) => {
                        const index = watchHistory.findIndex(m => m.tmdb_id === groupedMovie.tmdb_id)
                        if (index >= 0) {
                          cardRefs.current[index] = el
                        }
                      }}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      whileHover={{ scale: 1.05, y: -8 }}
                      transition={{ duration: 0.2 }}
                      className={`flex-shrink-0 ${isTV ? 'w-[180px] tv-card' : 'w-[280px] sm:w-[320px] md:w-[360px]'} group cursor-pointer relative ${isTV && focusedIndex === watchHistory.findIndex(m => m.tmdb_id === groupedMovie.tmdb_id) ? 'tv-focused' : ''}`}
                      tabIndex={isTV ? 0 : -1}
                      role="button"
                      aria-label={`Resume ${groupedMovie.title}`}
                      onClick={() => handleCardClick(groupedMovie)}
                      onFocus={() => {
                        const index = watchHistory.findIndex(m => m.tmdb_id === groupedMovie.tmdb_id)
                        if (index >= 0) {
                          // Immediately clear all other cards' focus state
                          setFocusedIndex(-1)
                          // Use requestAnimationFrame to ensure DOM updates happen
                          requestAnimationFrame(() => {
                            setFocusedIndex(index)
                          })
                        }
                      }}
                      onBlur={(e) => {
                        // Always clear when this card loses focus
                        // The new card's onFocus will set its own index
                        const index = watchHistory.findIndex(m => m.tmdb_id === groupedMovie.tmdb_id)
                        if (focusedIndex === index) {
                          setFocusedIndex(-1)
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleCardClick(groupedMovie)
                        }
                      }}
                    >
                      {/* Movie Backdrop */}
                      <div className="relative rounded-md overflow-hidden bg-gray-800 shadow-lg aspect-video">
                        {groupedMovie.backdrop_path ? (
                          <img
                            src={`${TMDB_IMAGE_BASE}${groupedMovie.backdrop_path}`}
                            alt={groupedMovie.title}
                            className="w-full h-full object-cover transition-transform group-hover:scale-110 duration-300"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-700">
                            <Play className="w-16 h-16 text-gray-500" />
                          </div>
                        )}

                        {/* Dark overlay on hover */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                        {/* Play button overlay */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                            <Play className="w-6 h-6 md:w-8 md:h-8 text-white fill-white" />
                          </div>
                        </div>

                        {/* Progress Bar - Always visible at bottom like Netflix/Prime */}
                        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gray-700/90">
                          <div
                            className="h-full bg-red-600 transition-all duration-300"
                            style={{ 
                              width: progressPercentage >= 95 ? '100%' : `${Math.max(progressPercentage, 1)}%`
                            }}
                          />
                        </div>

                        {/* Multiple Sources Badge - Top left, always visible */}
                        {groupedMovie.has_multiple_sources && (
                          <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-black/90 backdrop-blur-sm flex items-center gap-1.5">
                            <Layers className="w-3.5 h-3.5 text-blue-400" />
                            <span className="text-xs font-medium text-blue-400">
                              {groupedMovie.all_sources.length} sources
                            </span>
                          </div>
                        )}

                        {/* Remove Button - Only on hover */}
                        <button
                          onClick={(e) => handleRemove(e, groupedMovie.tmdb_id)}
                          className="absolute top-2 right-2 p-1.5 rounded-full bg-black/80 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10"
                          aria-label="Remove from watch history"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>

                        {/* Source Badge - Only on hover (for single source) */}
                        {!groupedMovie.has_multiple_sources && (
                          <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-black/80 text-xs font-medium text-white backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            {primarySource.source_type === 'vidfast' && 'VidFast'}
                            {primarySource.source_type === 'vidlink' && 'VidLink'}
                            {primarySource.source_type === 'filestream' && 'Filestream'}
                            {primarySource.source_type === 'gdrive' && 'Google Drive'}
                          </div>
                        )}

                        {/* Title overlay at bottom - Only on hover */}
                        <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <h3 className="text-white font-semibold text-sm md:text-base line-clamp-1 drop-shadow-lg">
                            {groupedMovie.title}
                          </h3>
                          {progressPercentage > 0 && (
                            <p className="text-gray-200 text-xs mt-0.5 drop-shadow-lg">
                              {progressPercentage}% complete
                            </p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>

      {/* Source Selection Dialog */}
      {/* Only show dialog on non-TV devices */}
      {!isTV && selectedMovie && (
        <SourceSelectionDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          movieTitle={selectedMovie.title}
          sources={selectedMovie.all_sources}
          onSourceSelect={resumePlayback}
        />
      )}
    </>
  )
}

